import http from './http';

// ── Roles ───────────────────────────────────────────────────────

export interface RoleInfo { key: string; label: string; count?: number }
export interface RoleUser {
  id: string; username: string; email: string; role: string;
  is_owner: boolean; is_active: boolean; created_at: string | null;
}

// ── Feature rollout ─────────────────────────────────────────────

export interface FeatureFlag {
  feature_key: string;
  label: string;
  description: string | null;
  category: string;
  default_enabled: boolean;
  dev_tester_enabled: boolean;
  effective_for_users: boolean;
  updated_at: string | null;
}

// ── Usage ───────────────────────────────────────────────────────

export interface UserUsage {
  user_id: string;
  username: string | null;
  email: string | null;
  role: string | null;
  storage: {
    used_bytes: number; file_count: number; quota_bytes: number;
    base_quota_bytes: number; purchased_quota_bytes: number;
    remaining_bytes: number; usage_percent: number | null; backend: string;
  };
  ai: { input_tokens: number; output_tokens: number; total_tokens: number; cost_usd: number; calls: number };
  support: { tickets: number };
}

export interface UsageOverview {
  totals: { users: number; storage_used_bytes: number; storage_quota_bytes: number; ai_tokens: number; ai_cost_usd: number };
  users: UserUsage[];
}

// ── Support tickets ─────────────────────────────────────────────

export interface TicketMessage {
  id: string; author_id: string | null; author_role: string; body: string;
  is_internal: boolean; created_at: string | null;
}
export interface Ticket {
  id: string; number: string; user_id: string; subject: string; body: string | null;
  category: string; priority: string; status: string; assigned_to: string | null;
  resolved_at: string | null; created_at: string | null; updated_at: string | null;
  messages?: TicketMessage[]; can_manage?: boolean;
}
export interface TicketStats {
  total: number; open: number; in_progress: number; waiting: number; resolved: number; closed: number;
}

export const portalGovernanceApi = {
  // roles
  roles: () => http.get<{ roles: RoleInfo[]; summary: RoleInfo[] }>('/customer-portal/roles').then(r => r.data),
  roleUsers: () => http.get<{ users: RoleUser[] }>('/customer-portal/roles/users').then(r => r.data.users),
  setRole: (userId: string, role: string) =>
    http.put(`/customer-portal/roles/users/${userId}`, { role }).then(r => r.data),

  // features
  features: () => http.get<{ features: FeatureFlag[] }>('/customer-portal/features').then(r => r.data.features),
  setFeature: (key: string, data: { default_enabled?: boolean; dev_tester_enabled?: boolean }) =>
    http.put<FeatureFlag>(`/customer-portal/features/${key}`, data).then(r => r.data),

  // usage
  usage: () => http.get<UsageOverview>('/customer-portal/usage').then(r => r.data),
  userUsage: (userId: string) => http.get<UserUsage>(`/customer-portal/usage/${userId}`).then(r => r.data),
  setQuota: (userId: string, data: { base_bytes?: number; purchased_bytes?: number }) =>
    http.put<UserUsage['storage']>(`/customer-portal/usage/${userId}/quota`, data).then(r => r.data),

  // tickets
  tickets: () => http.get<{ tickets: Ticket[]; stats: TicketStats }>('/customer-portal/tickets').then(r => r.data),
  myTickets: () => http.get<{ tickets: Ticket[]; stats: TicketStats }>('/customer-portal/tickets/mine').then(r => r.data),
  ticket: (id: string) => http.get<Ticket>(`/customer-portal/tickets/${id}`).then(r => r.data),
  createTicket: (data: { subject: string; body?: string; category?: string; priority?: string }) =>
    http.post<Ticket>('/customer-portal/tickets', data).then(r => r.data),
  replyTicket: (id: string, data: { body: string; is_internal?: boolean }) =>
    http.post<TicketMessage>(`/customer-portal/tickets/${id}/messages`, data).then(r => r.data),
  updateTicket: (id: string, data: { status?: string; priority?: string; assigned_to?: string }) =>
    http.put<Ticket>(`/customer-portal/tickets/${id}`, data).then(r => r.data),
};
